import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { dataCentralApi, authApiAuthed } from '@/api/client';
import { tokenStorage } from '@/lib/token-storage';

/**
 * Account / Information data layer. Mirrors the legacy
 * `@console/src/pages/Account/Information` behaviour but routes between the two
 * auth backends the console talks to:
 *  - **v4 DataCentral** (`/partner/auth/*`) for the unified change-password and
 *    the TOTP MFA endpoints.
 *  - **auth v2** (`/auth/v2/*`) for local-account password changes and the
 *    legacy email-MFA toggle.
 *
 * Responses come back in two envelope shapes — unified `{ result }` and legacy
 * `{ data }` — so the helpers below unwrap defensively.
 */

export const accountKeys = {
  mfaStatus: ['account', 'mfa', 'status'] as const,
};

/** Pull a human-readable error message out of an axios error, with a fallback. */
export function accountErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | {
          error_message?: string;
          message?: string;
          errors?: Array<{ message?: string }>;
        }
      | undefined;
    return (
      body?.error_message ||
      body?.message ||
      body?.errors?.[0]?.message ||
      error.message ||
      fallback
    );
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

/**
 * Change the account password. Keycloak sessions (SSO + Google) hit the unified
 * v4 endpoint; local accounts hit auth v2 (which names the current password
 * `password`).
 */
export function useChangePassword(
  options?: UseMutationOptions<void, unknown, ChangePasswordRequest>,
) {
  return useMutation({
    mutationFn: async (body: ChangePasswordRequest) => {
      if (tokenStorage.authMethod !== 'local') {
        await dataCentralApi.post('/partner/auth/change-password', body);
      } else {
        await authApiAuthed.post('/auth/v2/change_password', {
          password: body.current_password,
          new_password: body.new_password,
          new_password_confirmation: body.new_password_confirmation,
        });
      }
    },
    ...options,
  });
}

// ---------------------------------------------------------------------------
// MFA — status
// ---------------------------------------------------------------------------

export type MfaMethod = 'totp' | 'email' | null;

export interface MfaStatus {
  enabled: boolean;
  method: MfaMethod;
}

export function useMfaStatus() {
  return useQuery<MfaStatus>({
    queryKey: accountKeys.mfaStatus,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/auth/mfa/status');
      const r = res.data?.result ?? res.data?.data ?? {};
      const method = (r.method ?? null) as MfaMethod;
      return { enabled: Boolean(r.enabled), method };
    },
    staleTime: 60_000,
    retry: 1,
  });
}

function useMfaInvalidation() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: accountKeys.mfaStatus });
}

// ---------------------------------------------------------------------------
// MFA — TOTP (authenticator app)
// ---------------------------------------------------------------------------

export interface OtpSetup {
  secret: string;
  qr_code: string;
  provisioning_uri: string;
  backup_codes: string[];
}

/** Step 1: provision a TOTP secret + QR + backup codes (nothing persisted yet). */
export function useSetupMfaOtp(
  options?: UseMutationOptions<OtpSetup, unknown, void>,
) {
  return useMutation({
    mutationFn: async () => {
      const res = await dataCentralApi.post('/partner/auth/mfa/otp/setup', {});
      const r = res.data?.result ?? res.data?.data ?? {};
      return {
        secret: r.secret ?? '',
        qr_code: r.qr_code ?? '',
        provisioning_uri: r.provisioning_uri ?? '',
        backup_codes: r.backup_codes ?? [],
      } as OtpSetup;
    },
    ...options,
  });
}

export interface VerifyOtpRequest {
  secret: string;
  otp_code: string;
  backup_codes: string[];
}

/** Step 2: verify the first code and enable TOTP. */
export function useVerifyMfaOtp(
  options?: UseMutationOptions<void, unknown, VerifyOtpRequest>,
) {
  const invalidate = useMfaInvalidation();
  return useMutation({
    mutationFn: async (body: VerifyOtpRequest) => {
      await dataCentralApi.post('/partner/auth/mfa/otp/verify', body);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

/** Disable TOTP — requires the account password to confirm. */
export function useDisableMfaOtp(
  options?: UseMutationOptions<void, unknown, { password: string }>,
) {
  const invalidate = useMfaInvalidation();
  return useMutation({
    mutationFn: async (body: { password: string }) => {
      await dataCentralApi.post('/partner/auth/mfa/otp/disable', body);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

// ---------------------------------------------------------------------------
// MFA — email (legacy auth v2 toggle; local accounts only)
// ---------------------------------------------------------------------------

export interface ToggleEmailMfaRequest {
  enable: boolean;
  /** Email address to receive codes — required when enabling. */
  email?: string;
}

export function useToggleEmailMfa(
  options?: UseMutationOptions<void, unknown, ToggleEmailMfaRequest>,
) {
  const invalidate = useMfaInvalidation();
  return useMutation({
    mutationFn: async ({ enable, email }: ToggleEmailMfaRequest) => {
      await authApiAuthed.post('/auth/v2/update_mfa', {
        mfa: enable
          ? { mfa: true, mfa_method: 'email', mfa_information: email }
          : {
              mfa: false,
              mfa_method: null,
              mfa_information: null,
              mfa_backup: null,
            },
      });
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}
